import type { OrganizationSnapshot, StructureProposal } from './model';

export type ProposalKind = 'divisions' | 'teams' | 'positions';
export interface ProposalDuplicate {
  kind: ProposalKind;
  id: string;
  targetId: string;
  targetName: string;
  existing: boolean;
}
const normalized = (value: string) => value.trim().toLocaleLowerCase();
const same = (left?: string | null, right?: string | null) => (left || '') === (right || '');

export function proposalDuplicates(
  proposal: StructureProposal,
  snapshot: OrganizationSnapshot
): ProposalDuplicate[] {
  const duplicates: ProposalDuplicate[] = [];
  for (const kind of ['divisions', 'teams', 'positions'] as const) {
    const proposed = proposal[kind];
    proposed.forEach((item, index) => {
      const name = 'title' in item ? item.title : item.name;
      const matches = (candidate: typeof item) => {
        const candidateName = 'title' in candidate ? candidate.title : candidate.name;
        if (normalized(name) !== normalized(candidateName)) return false;
        if ('parentDivisionId' in item || kind === 'divisions')
          return same((item as any).parentDivisionId, (candidate as any).parentDivisionId);
        if (kind === 'teams')
          return (
            same((item as any).divisionId, (candidate as any).divisionId) &&
            same((item as any).parentTeamId, (candidate as any).parentTeamId)
          );
        return (
          same((item as any).divisionId, (candidate as any).divisionId) &&
          same((item as any).teamId, (candidate as any).teamId) &&
          same((item as any).parentPositionId, (candidate as any).parentPositionId)
        );
      };
      const existing = snapshot[kind].find((candidate) => matches(candidate as typeof item));
      const target = existing || proposed.slice(0, index).find(matches);
      if (target && normalized(name))
        duplicates.push({
          kind,
          id: item.id,
          targetId: target.id,
          targetName: 'title' in target ? target.title : target.name,
          existing: !!existing,
        });
    });
  }
  return duplicates;
}

/** Removing a suggestion leaves its dependents visible, with that relationship cleared. */
export function replaceProposalReference(
  proposal: StructureProposal,
  kind: ProposalKind,
  id: string,
  targetId: string | null
): StructureProposal {
  const replace = (value?: string | null) => (value === id ? targetId : value);
  return {
    divisions: proposal.divisions
      .filter((item) => kind !== 'divisions' || item.id !== id)
      .map((item) => ({
        ...item,
        parentDivisionId:
          kind === 'divisions' ? replace(item.parentDivisionId) : item.parentDivisionId,
      })),
    teams: proposal.teams
      .filter((item) => kind !== 'teams' || item.id !== id)
      .map((item) => ({
        ...item,
        divisionId: kind === 'divisions' ? replace(item.divisionId) : item.divisionId,
        parentTeamId: kind === 'teams' ? replace(item.parentTeamId) : item.parentTeamId,
      })),
    positions: proposal.positions
      .filter((item) => kind !== 'positions' || item.id !== id)
      .map((item) => ({
        ...item,
        divisionId: kind === 'divisions' ? replace(item.divisionId) : item.divisionId,
        teamId: kind === 'teams' ? replace(item.teamId) : item.teamId,
        parentPositionId:
          kind === 'positions' ? replace(item.parentPositionId) : item.parentPositionId,
      })),
  };
}

export function proposalProblems(
  proposal: StructureProposal,
  snapshot: OrganizationSnapshot
): string[] {
  const problems: string[] = [];
  const allIds = [...proposal.divisions, ...proposal.teams, ...proposal.positions].map(
    (item) => item.id
  );
  if (new Set(allIds).size !== allIds.length)
    problems.push('Each suggestion needs a unique identifier. Generate suggestions again.');
  const existingIds = new Set(
    [...snapshot.divisions, ...snapshot.teams, ...snapshot.positions].map((item) => item.id)
  );
  if (allIds.some((id) => existingIds.has(id)))
    problems.push(
      'A suggestion identifier is already in the current structure. Resolve the matching item or regenerate suggestions.'
    );
  if (
    [...proposal.divisions, ...proposal.teams].some((item) => !item.name.trim()) ||
    proposal.positions.some((item) => !item.title.trim())
  )
    problems.push('Give every suggestion a name.');
  if (proposalDuplicates(proposal, snapshot).length)
    problems.push('Resolve duplicate suggestions by renaming them or using the matching item.');
  for (const [kind, parentKey] of [
    ['divisions', 'parentDivisionId'],
    ['teams', 'parentTeamId'],
    ['positions', 'parentPositionId'],
  ] as const) {
    const items = [...snapshot[kind], ...proposal[kind]];
    const parents = new Map(
      items.map((item) => [item.id, (item as any)[parentKey] as string | undefined])
    );
    if (
      proposal[kind].some((item) => {
        const visited = new Set<string>();
        let current: string | undefined = item.id;
        while (current) {
          if (visited.has(current)) return true;
          visited.add(current);
          current = parents.get(current);
        }
        return false;
      })
    )
      problems.push('Reporting and structure relationships cannot contain a loop.');
  }
  const divisions = new Set([...snapshot.divisions, ...proposal.divisions].map((item) => item.id));
  const teams = new Map([...snapshot.teams, ...proposal.teams].map((item) => [item.id, item]));
  const positions = new Set([...snapshot.positions, ...proposal.positions].map((item) => item.id));
  if (
    proposal.divisions.some(
      (item) => item.parentDivisionId && !divisions.has(item.parentDivisionId)
    ) ||
    proposal.teams.some(
      (item) =>
        (item.divisionId && !divisions.has(item.divisionId)) ||
        (item.parentTeamId && !teams.has(item.parentTeamId))
    ) ||
    proposal.positions.some(
      (item) =>
        (item.divisionId && !divisions.has(item.divisionId)) ||
        (item.teamId && !teams.has(item.teamId)) ||
        (item.parentPositionId && !positions.has(item.parentPositionId))
    )
  )
    problems.push('A linked item is missing. Review the suggestion relationships.');
  if (
    proposal.positions.some(
      (item) => item.teamId && !same(item.divisionId, teams.get(item.teamId)?.divisionId)
    )
  )
    problems.push('A position must use the same division as its selected team.');
  if (
    proposal.teams.some(
      (item) =>
        item.parentTeamId && !same(item.divisionId, teams.get(item.parentTeamId)?.divisionId)
    )
  )
    problems.push('A nested team must use the same division as its parent team.');
  return [...new Set(problems)];
}
